import './PersonProfile.css'

import type { ReactNode } from 'react'

import type { MatchDecision, MatchHighlight } from '../store/matches'
import type { ProfileAttributes } from '../store/profile'

import { usePhotoObjectUrls } from '../hooks/usePhotoObjectUrl'
import { cx } from '../utilities/cx'
import { Button } from './Button'
import { ImageGallery } from './ImageGallery'
import { Tooltip } from './Tooltip'

export type PersonProfileMatchMeta = {
  score: number
  theirScore: number
  yourScore: number
  status: string
  yourDecision: MatchDecision
  awaitingThem: boolean
  isSimulated?: boolean
  matchSummary?: string
  highlights?: MatchHighlight[]
}

export type PersonProfileProps = {
  className?: string
  name: string
  bio?: string
  birthday?: string
  location?: string
  photoIds: string[]
  variant: 'self' | 'match'
  attributes?: ProfileAttributes | null
  onEditPhotos?: () => void
  onEditProfile?: () => void
  matchMeta?: PersonProfileMatchMeta
  onInterested?: () => void
  onUnmatch?: () => void
  onMessage?: () => void
  onAskRheya?: () => void
  safetySlot?: ReactNode
  galleryTargetHeight?: number
}

const domainLabels: Record<string, string> = {
  intent: "what you're looking for",
  kids: 'kids',
  substances: 'lifestyle',
  interests: 'interests',
  politics: 'politics',
  religion: 'faith',
}

function completenessCopy(
  complete: string[] | undefined,
  missing: string[] | undefined,
  partial: string[] | undefined,
): string {
  const filled = complete?.length ?? 0
  const leftover = [...(partial ?? []), ...(missing ?? [])]
    .map((key) => domainLabels[key] ?? key)
    .filter(Boolean)
  if (filled >= 6) return 'Rheya has what she needs to introduce you well.'
  if (leftover.length === 0) return `${filled} of 6 matchmaking areas filled.`
  if (leftover.length === 1) return `Rheya still wants to know you better — ${leftover[0]}.`
  if (leftover.length === 2)
    return `Rheya still wants to know you better — ${leftover[0]} and ${leftover[1]}.`
  return `Rheya still wants to know you better — ${leftover.slice(0, -1).join(', ')}, and ${leftover[leftover.length - 1]}.`
}

function calculateAge(birthday: string | undefined): number | null {
  if (!birthday) return null
  const birthDate = new Date(birthday)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1
  return age
}

function formatPercent(score: number): string {
  return `${Math.round(score * 100)}%`
}

export const PersonProfile = (props: PersonProfileProps) => {
  const {
    className,
    name,
    bio,
    birthday,
    location,
    photoIds = [],
    variant,
    attributes,
    onEditPhotos,
    onEditProfile,
    matchMeta,
    onInterested,
    onUnmatch,
    onMessage,
    onAskRheya,
    safetySlot,
    galleryTargetHeight = 400,
  } = props

  const age = calculateAge(birthday)
  const objectUrls = usePhotoObjectUrls(photoIds, 1200)
  const images = objectUrls.filter((url): url is string => !!url)

  const isMatch = variant === 'match'
  const isActive = matchMeta?.status === 'active'
  const isConnected = matchMeta?.status === 'connected'
  const isInterested = matchMeta?.yourDecision === 'interested' || Boolean(matchMeta?.awaitingThem)

  const chips = (isMatch ? matchMeta?.highlights : undefined) ?? attributes?.chips ?? []
  const completion = variant === 'self' ? attributes?.completion : undefined

  const hasMatchActions =
    isMatch && ((isActive && !isInterested) || (isActive && isInterested) || isConnected)

  return (
    <div className={cx('person-profile', `person-profile--${variant}`, className)}>
      <section className="person-profile__gallery" aria-label={`${name} photos`}>
        <ImageGallery
          images={images}
          alt={name}
          targetHeight={galleryTargetHeight}
          {...(variant === 'self' && onEditPhotos && { onEdit: onEditPhotos })}
        />
      </section>

      <div className="person-profile__body">
        <header className="person-profile__header">
          <div className="person-profile__identity">
            <h1 className="person-profile__name">
              {name}
              {age !== null && <span className="person-profile__age">, {age}</span>}
            </h1>

            <div className="person-profile__meta">
              {location && <span className="person-profile__location">{location}</span>}
              {isMatch && matchMeta && (
                <>
                  {location && <span className="person-profile__meta-sep" aria-hidden />}
                  <span className="person-profile__match-score">
                    {formatPercent(matchMeta.score)} match
                  </span>
                  {matchMeta.isSimulated && (
                    <span className="person-profile__badge person-profile__badge--sim">
                      Simulated
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {variant === 'self' && onEditProfile && (
            <div className="person-profile__header-actions">
              <Button kind="ghost" text="Edit Profile" onClick={onEditProfile} />
            </div>
          )}

          {hasMatchActions && (
            <div className="person-profile__header-actions person-profile__header-actions--match">
              {isActive && !isInterested && (
                <>
                  <Button kind="ghost" text="Unmatch" onClick={onUnmatch} />
                  <Button kind="primary" text="Interested" onClick={onInterested} />
                </>
              )}
              {isActive && isInterested && (
                <Button kind="ghost" text="Unmatch" onClick={onUnmatch} />
              )}
              {isConnected && (
                <>
                  <Button kind="ghost" text="Unmatch" onClick={onUnmatch} />
                  {onMessage && <Button kind="primary" text="Message" onClick={onMessage} />}
                </>
              )}
            </div>
          )}
        </header>

        {(isConnected || (isActive && isInterested)) && (
          <div className="person-profile__notices">
            {isConnected && (
              <div className="person-profile__status person-profile__status--connected">
                You&apos;re connected with {name}!
              </div>
            )}
            {isActive && isInterested && (
              <div className="person-profile__status person-profile__status--waiting">
                Interest shared—waiting for them
              </div>
            )}
          </div>
        )}

        {isMatch && (matchMeta?.matchSummary || onAskRheya) && (
          <section className="person-profile__rheya" aria-label="Note from Rheya">
            <p className="person-profile__rheya-label">Rheya</p>
            {matchMeta?.matchSummary && (
              <p className="person-profile__rheya-brief">{matchMeta.matchSummary}</p>
            )}
            {onAskRheya && (
              <Button
                className="person-profile__rheya-ask"
                kind="ghost"
                text="Ask Rheya about this match"
                onClick={onAskRheya}
              />
            )}
          </section>
        )}

        {variant === 'self' && completion && (
          <section className="person-profile__completeness" aria-label="Profile completeness">
            <p className="person-profile__completeness-text">
              {completenessCopy(completion.complete, completion.missing, completion.partial)}
            </p>
          </section>
        )}

        <div className="person-profile__content">
          <div className="person-profile__main">
            {bio && <p className="person-profile__bio">{bio}</p>}

            {chips.length > 0 && (
              <div
                className="person-profile__chips"
                aria-label={isMatch ? 'Why you fit' : 'About you'}
              >
                {chips.map((chip) => (
                  <span key={`${chip.key}-${chip.text}`} className="person-profile__chip">
                    {chip.text}
                  </span>
                ))}
              </div>
            )}
          </div>

          {isMatch && matchMeta && (
            <aside className="person-profile__aside" aria-label="Match details">
              <div className="person-profile__compatibility">
                <div className="person-profile__compatibility-header">
                  <span className="person-profile__compatibility-side">
                    <span className="person-profile__compatibility-caption">Theirs</span>
                    <span className="person-profile__compatibility-value">
                      {formatPercent(matchMeta.theirScore)}
                    </span>
                  </span>
                  <span className="person-profile__compatibility-label">Compatibility</span>
                  <span className="person-profile__compatibility-side person-profile__compatibility-side--yours">
                    <span className="person-profile__compatibility-caption">Yours</span>
                    <span className="person-profile__compatibility-value">
                      {formatPercent(matchMeta.yourScore)}
                    </span>
                  </span>
                </div>
                <div className="person-profile__compatibility-bar">
                  <div
                    className="person-profile__compatibility-fill person-profile__compatibility-fill--theirs"
                    style={{ width: formatPercent(matchMeta.theirScore) }}
                  />
                  <div className="person-profile__compatibility-center" />
                  <div
                    className="person-profile__compatibility-fill person-profile__compatibility-fill--yours"
                    style={{ width: formatPercent(matchMeta.yourScore) }}
                  />
                </div>
                <Tooltip
                  className="person-profile__compatibility-overlay person-profile__compatibility-overlay--left"
                  content="How well they fit you"
                  position="top"
                >
                  <div className="person-profile__compatibility-overlay-target" />
                </Tooltip>
                <Tooltip
                  className="person-profile__compatibility-overlay person-profile__compatibility-overlay--right"
                  content="How well you fit them"
                  position="top"
                >
                  <div className="person-profile__compatibility-overlay-target" />
                </Tooltip>
              </div>
            </aside>
          )}
        </div>

        {isMatch && safetySlot && <footer className="person-profile__safety">{safetySlot}</footer>}
      </div>
    </div>
  )
}
